<template>
    <div class="vehicle-insurance">
        <ul v-if="!hasPlatformInsurance">
            <div class="vehicle-insurance__comprehensive-cover">
                {{ $t('pages.vehicle.comprehensive_cover_by_owner') }}
            </div>
            <p class="vehicle-insurance__provider-description">
                {{ $t('pages.vehicle.comprehensive_cover_by_owner_description') }}
            </p>
            <OverviewListItem v-if="vehicle.insuranceCompany" :vehicle="vehicle" field="insuranceCompany" />
            <li class="overview-list-item">
                <label class="overview-list-item__label">{{ $t('pages.vehicle.insurance_cost_included_label') }}</label>
                <span class="overview-list-item__value">
                    {{ $t('pages.vehicle.insurance_cost_included_text') }}
                </span>
            </li>
            <li v-if="showRoadsideAssistanceForOwnInsurance" class="overview-list-item">
                <label class="overview-list-item__label">{{ $t('vehicle_fields.roadside_assistance_label') }}</label>
                <span class="overview-list-item__value"> {{ roadsideAssistanceLabel }} </span>
            </li>
            <li v-if="vehicle.isConsideredSwiss && vehicle.countryAllowsPlatformInsurance" class="overview-list-item">
                <label class="overview-list-item__label">{{ $t('pages.vehicle.interior_insurance_label') }}</label>
                <span class="overview-list-item__value">
                    {{ interiorInsuranceLabel }}
                </span>
            </li>
            <OverviewListItem :vehicle="vehicle" field="deductible" />
        </ul>
        <template v-if="hasPlatformInsurance">
            <ul>
                <div class="vehicle-insurance__provider">
                    <span>{{ platformInsuranceInfo.title }}</span>
                    <InsuranceLogo :vendor="insuranceVendor" class="vehicle-insurance__logo" />
                </div>

                <p class="vehicle-insurance__provider-description">
                    {{ platformInsuranceInfo.description }}
                </p>

                <li class="overview-list-item">
                    <label class="overview-list-item__label">
                        {{ $t('pages.vehicle.insurance_cost_included_label') }}
                    </label>
                    <span class="overview-list-item__value">
                        {{ $t('pages.vehicle.insurance_cost_included_text') }}
                    </span>
                </li>

                <li class="overview-list-item">
                    <label class="overview-list-item__label">
                        {{ $t('vehicle_fields.roadside_assistance_label') }}
                    </label>
                    <span v-if="platformInsuranceCompany === BALOISE" class="overview-list-item__value">
                        {{ roadsideAssistanceLabel }}
                    </span>
                    <span v-else class="overview-list-item__value">
                        {{ $t('pages.vehicle.roadside_assistance_included') }}
                    </span>
                </li>

                <li v-if="platformInsuranceCompany === OMOCOM" class="overview-list-item">
                    <label class="overview-list-item__label">
                        {{ $t('vehicle_fields.omocom_interior_insurance_label') }}
                    </label>
                    <span class="overview-list-item__value">
                        {{ omocomInteriorInsuranceLabel }}
                    </span>
                </li>

                <li v-if="platformInsuranceCompany === BALOISE" class="overview-list-item">
                    <label class="overview-list-item__label">{{ $t('pages.vehicle.interior_insurance_label') }}</label>
                    <span class="overview-list-item__value">
                        {{ interiorInsuranceLabel }}
                    </span>
                </li>

                <li v-if="isOmocomInsurance && deductibleValues" class="overview-list-item">
                    <label class="overview-list-item__label">{{ $t('vehicle_fields.deductible_label') }}</label>
                    <span class="overview-list-item__value">
                        {{
                            $t('pages.vehicle.deductible_value', {
                                minDeductible: deductibleValues.minDeductible,
                                maxDeductible: deductibleValues.maxDeductible,
                            })
                        }}
                        <svg-icon v-tooltip="$t('pages.vehicle.deductible_tooltip')" name="info-outline" />
                    </span>
                </li>
                <template v-else>
                    <li class="overview-list-item overview-list-item--wrapped">
                        <label class="overview-list-item__label">{{ $t('vehicle_fields.max_deductible_label') }}</label>
                        <span class="overview-list-item__value">
                            {{
                                defaultMaxDeductible
                                    | price
                                    | formatAsPrice(vehicle.currency, ['eur', 'chf'].includes(defaultCurrency) ? 2 : 0)
                            }}
                        </span>
                        <span v-if="userCurrency !== vehicle.currency" class="overview-list-item__price-before">
                            {{
                                $t('pricing.converted_from', {
                                    price: formattedPrice(
                                        defaultMaxDeductible,
                                        vehicle.currency,
                                        ['eur', 'chf'].includes(defaultCurrency) ? 2 : 0,
                                        false
                                    ),
                                })
                            }}
                        </span>
                    </li>
                    <li v-if="hasDeductibleBuydownAddon" class="overview-list-item overview-list-item--wrapped">
                        <label class="overview-list-item__label">{{ $t('pricing.deductible_buydown') }}</label>
                        <span class="overview-list-item__value">
                            {{
                                $t('pages.vehicle.insurance_deductible_buydown_info', {
                                    loweredDeductible: formattedPrice(
                                        maxDeductibleAfterBuydown,
                                        vehicle.currency,
                                        ['eur', 'chf'].includes(defaultCurrency) ? 2 : 0
                                    ),
                                })
                            }}
                        </span>
                        <span v-if="userCurrency !== vehicle.currency" class="overview-list-item__price-before">
                            {{
                                $t('pricing.converted_from', {
                                    price: formattedPrice(
                                        maxDeductibleAfterBuydown,
                                        vehicle.currency,
                                        ['eur', 'chf'].includes(defaultCurrency) ? 2 : 0,
                                        false
                                    ),
                                })
                            }}
                        </span>
                    </li>
                </template>
            </ul>
            <p class="section__description">
                <LocalizedLink :to="{ name: readMoreRoute }" target="_blank">
                    {{ $t('pages.vehicle.insurance_read_more_link') }}
                </LocalizedLink>
            </p>
        </template>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import OverviewListItem from '~/components/vehicle/OverviewListItem.vue';
import { INSURANCE_ADDON } from '~/config/productTypes';
import { OMOCOM, BALOISE } from '~/config/insurance';
import InsuranceLogo from '~/components/InsuranceLogo.vue';

export default {
    components: {
        OverviewListItem,
        InsuranceLogo,
    },
    props: {
        vehicle: {
            type: Object,
            default: () => {},
        },
        deductibleValues: {
            type: Object,
            default: () => {},
        },
        addons: {
            type: Array,
            default: null,
        },
        isOmocomInsurance: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        const insuranceVendor = this.vehicle.insuranceVendor || {};

        return {
            insuranceVendor: insuranceVendor || {},
            maxDeductibleAfterBuydown: insuranceVendor.maxDeductibleAfterBuydown || null,
            defaultMaxDeductible: insuranceVendor.defaultMaxDeductible || null,
            isLoading: false,
            OMOCOM,
            BALOISE,
        };
    },
    computed: {
        ...mapGetters(['defaultCurrency', 'userCurrency']),
        hasPlatformInsurance() {
            return this.vehicle.hasPlatformInsurance;
        },
        platformInsuranceCompany() {
            if (this.insuranceVendor?.id?.includes(OMOCOM)) {
                return OMOCOM;
            } else if (this.insuranceVendor?.id?.includes(BALOISE)) {
                return BALOISE;
            }

            return null;
        },
        platformInsuranceInfo() {
            if (this.platformInsuranceCompany === OMOCOM) {
                return {
                    title: this.$t('pages.vehicle.omocom_insurance.title'),
                    description: this.$t('pages.vehicle.omocom_insurance.description'),
                };
            } else if (this.platformInsuranceCompany === BALOISE) {
                return {
                    title: this.$t('pages.vehicle.baloise_insurance.title'),
                    description: this.$t('pages.vehicle.baloise_insurance.description'),
                };
            }

            return '';
        },
        showRoadsideAssistanceForOwnInsurance() {
            if (this.vehicle.isConsideredSwiss) {
                return this.vehicle.countryAllowsPlatformInsurance || this.vehicle.insuranceIncludesRoadsideAssistance;
            }

            if (this.vehicle?.isOmocomMarket && this.vehicle.insuranceIncludesRoadsideAssistance) {
                return true;
            }

            return false;
        },
        interiorInsuranceLabel() {
            if (this.vehicle.mandatoryAddons && this.vehicle.mandatoryAddons[INSURANCE_ADDON.interior_insurance]) {
                return this.$t('pages.vehicle.interior_insurance_included');
            } else {
                return this.$t('pages.vehicle.interior_insurance_text');
            }
        },
        omocomInteriorInsuranceLabel() {
            if (this.vehicle.mandatoryAddons && this.vehicle.mandatoryAddons[INSURANCE_ADDON.interior_insurance]) {
                return this.$t('pages.vehicle.omocom_interior_insurance_included');
            } else {
                return this.$t('pages.vehicle.omocom_interior_insurance_not_included');
            }
        },
        roadsideAssistanceLabel() {
            if (this.vehicle.insuranceIncludesRoadsideAssistance) {
                return this.$t('pages.vehicle.roadside_assistance_included');
            } else {
                return this.$t('pages.vehicle.roadside_assistance_can_be_added');
            }
        },
        hasDeductibleBuydownAddon() {
            return this.addons?.some(p => p.key === INSURANCE_ADDON.deductible_buydown);
        },
        readMoreRoute() {
            if (this.platformInsuranceCompany === OMOCOM) {
                return 'insurance-omocom';
            } else if (this.platformInsuranceCompany === BALOISE) {
                return 'insurance-baloise';
            } else {
                return 'insurance-comprehensive-cover-owner';
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@/sass/_variables.scss';

.vehicle-insurance {
    ul {
        .overview-list-item {
            &:last-of-type {
                margin-bottom: 0;
            }

            &--wrapped {
                flex-wrap: wrap;
            }

            &__price-before {
                width: 100%;
                margin-top: 5px;
                @include font-size(12px);
            }
        }
    }

    &__comprehensive-cover {
        @include font-size(16px);
        font-weight: bold;
        margin-bottom: 20px;

        @include md {
            margin-bottom: 24px;
        }
    }

    &__provider {
        display: flex;
        justify-content: space-between;
        align-items: center;
        @include font-size(16px);
        margin-bottom: 20px;

        span {
            font-weight: bold;
        }

        @include md {
            margin-bottom: 24px;
        }
    }

    &__provider-description {
        @include font-size(16px);
        margin-bottom: 20px;

        @include md {
            margin-bottom: 24px;
        }
    }

    &__logo {
        width: 80px;
    }

    .section {
        &__description {
            margin-top: 20px;
            margin-bottom: 0;

            @include md {
                margin-top: 24px;
            }
        }
    }
}
</style>
